import React, { useEffect } from "react";
import { message, Select, Table, TableColumnsType } from "antd";
import { TransactionHistoryFilter, Transaction } from "common/interfaces";
import PaginateListMobile from "components/PaginateListMobile";
import TransactionHistoryItem from "components/TransactionHistoryItem";
import { Controller, useForm } from "react-hook-form";
import { usePagination } from "common/hooks/usePagination";
import { useGetTransactionHistoriesQuery } from "reducers/transactionApi";
import { parseErrorMessage } from "common/utils";
import { Link } from "react-router-dom";
import { UTCToJapanTime } from "common/helper/time.helper";
import {
  PRODUCT_CATEGORY,
  TRANSACTION_HISTORY_FILTER_SORT,
} from "common/constants";
import { isEmpty } from "lodash";
import { useCommonContext } from "contexts/CommonContext";
import {
  TransactionStatusEnum,
  TransactionStatusLabel,
} from "common/enums/TransactionStatus.enum";

const columns: TableColumnsType<Transaction> = [
  {
    title: "取引ID",
    dataIndex: "id",
    align: "center",
    render: (value: number, record: Transaction) => (
      <Link
        to={`/seller/product/${record.product.code}/transaction/${record.buyer_id}/${record.seller_id}`}
        className="text-primary"
      >
        #{record.transaction.code}
      </Link>
    ),
  },
  {
    title: "出品ID",
    dataIndex: "product",
    align: "center",
    render: (value) => (
      <Link to={`/seller/product/${value.code}`} className="text-primary">
        #{value.code}
      </Link>
    ),
  },
  {
    title: "商品名",
    dataIndex: "product",
    align: "center",
    render: (value) => value.product_name,
  },
  {
    title: "買い手企業",
    dataIndex: "buyer",
    align: "center",
    render: (value) => value.company_name,
  },
  {
    title: "ステータス",
    dataIndex: "status",
    align: "center",
    render: (value: TransactionStatusEnum, record: Transaction) =>
      record.deleted_at ? "削除された" : TransactionStatusLabel[value],
  },
  {
    title: "最終更新",
    dataIndex: "created_at",
    align: "center",
    render: (value) => {
      const timeJapan = UTCToJapanTime(value);
      return `${timeJapan.year}/${timeJapan.month}/${timeJapan.day} ${timeJapan.hour}:${timeJapan.minute}`;
    },
  },
  {
    title: "取引画面",
    dataIndex: "product",
    align: "center",
    render: (value, record: Transaction) => (
      <Link
        to={`/seller/product/${value.code}/transaction/${record.buyer_id}/${record.seller_id}`}
        className="text-primary"
      >
        取引画面へ
      </Link>
    ),
  },
];

const TransactionHistory = () => {
  const { setHeaderTitle } = useCommonContext();
  const defaultValues: TransactionHistoryFilter = {
    search: "",
    type: [],
    status: [],
    sort: TRANSACTION_HISTORY_FILTER_SORT.FINAL_UPDATE.value,
    deleted_at: 2,
  };
  const { control, watch } = useForm<TransactionHistoryFilter>({
    defaultValues,
  });
  const {
    pagination,
    paginationMobile,
    onChangePage,
    updateTotal,
    updateTotalMobile,
  } = usePagination();

  const transactionHistoryList: TransactionHistoryFilter = watch();

  const { data: transactionHistoryData, error: inquiryErrClose } =
    useGetTransactionHistoriesQuery({
      filter: transactionHistoryList,
      page: pagination.current,
    });

  const { data: transactionHistoryMobileData, error: inquiryMobileErrClose } =
    useGetTransactionHistoriesQuery({
      filter: transactionHistoryList,
      page: paginationMobile.current,
    });

  useEffect(() => {
    if (transactionHistoryData?.data?.meta?.total_items) {
      updateTotal(transactionHistoryData.data.meta.total_items);
    }
  }, [transactionHistoryData]);

  useEffect(() => {
    if (transactionHistoryMobileData?.data?.meta?.total_items) {
      updateTotalMobile(transactionHistoryMobileData.data.meta.total_items);
    }
  }, [transactionHistoryMobileData]);

  useEffect(() => {
    if (inquiryErrClose) void message.error(parseErrorMessage(inquiryErrClose));
  }, [inquiryErrClose]);

  useEffect(() => {
    if (inquiryMobileErrClose)
      void message.error(parseErrorMessage(inquiryMobileErrClose));
  }, [inquiryMobileErrClose]);

  useEffect(() => {
    setHeaderTitle("取引履歴の閲覧");
  }, []);

  return (
    <div className="self-stretch min-h-[924px] p-[20px] pb-[100px] md:p-10 bg-white inline-flex flex-col justify-start items-start gap-[20px] md:gap-10 overflow-hidden">
      <div className="self-stretch flex flex-col justify-start items-start gap-7">
        <div className="self-stretch flex flex-col justify-start items-start gap-5">
          <div className="inline-flex justify-start items-center gap-5">
            <div className="justify-start text-black text-[15px] md:text-base font-medium font-['Noto_Sans_JP'] leading-relaxed">
              表示期間
            </div>
            <div className="min-w-[200px] flex-1 flex flex-col md:flex-row justify-start items-start gap-x-[15px] gap-y-[10px] md:gap-7 flex-wrap content-start">
              <Controller
                control={control}
                name="type"
                render={({ field }) => (
                  <Select
                    {...field}
                    allowClear
                    options={Object.values(PRODUCT_CATEGORY)}
                    placeholder="全期間"
                    className="w-full h-[50px]"
                  />
                )}
              />
            </div>
          </div>
          <Table<Transaction>
            className="w-full hidden md:block"
            rowKey="code"
            onChange={onChangePage}
            pagination={
              !isEmpty(transactionHistoryData?.data?.items) ? pagination : false
            }
            columns={columns}
            dataSource={transactionHistoryData?.data?.items || []}
          />
          <PaginateListMobile<Transaction>
            pagination={
              !isEmpty(transactionHistoryMobileData?.data?.items)
                ? paginationMobile
                : false
            }
            dataSource={transactionHistoryMobileData?.data?.items || []}
            renderItem={(item, checked, onSelect) => (
              <TransactionHistoryItem<Transaction>
                transaction={item}
                checked={checked}
                onSelect={onSelect}
                hideSelect={true}
              />
            )}
          />
        </div>
      </div>
    </div>
  );
};

export default TransactionHistory;
